import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API } from '../config';


function renderMarkdown(md) {
  const blocks = md.split('\n\n');
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    // Skip "Related documents" blockquote
    if (trimmed.startsWith('> Related documents') || trimmed.startsWith('> Documents connexes')) return null;
    if (trimmed.startsWith('---')) return <hr key={i} className="border-border my-6" />;
    if (trimmed.startsWith('# ')) return <h1 key={i} className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">{trimmed.slice(2)}</h1>;
    if (trimmed.startsWith('## ')) return <h2 key={i} className="font-heading text-xl sm:text-2xl font-semibold text-foreground mt-10 mb-3">{trimmed.slice(3)}</h2>;
    if (trimmed.startsWith('### ')) return <h3 key={i} className="font-heading text-lg font-medium text-foreground mt-8 mb-2">{trimmed.slice(4)}</h3>;
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const items = trimmed.split('\n').filter(l => l.match(/^[-*] /));
      return (
        <ul key={i} className="space-y-1.5 ml-4 my-3">
          {items.map((item, j) => (
            <li key={j} className="text-foreground/80 text-sm leading-relaxed flex items-start gap-2">
              <span className="text-blue-400 mt-1">•</span>
              <span>{item.replace(/^[-*] /, '')}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (trimmed.startsWith('>')) {
      return <blockquote key={i} className="border-l-2 border-blue-500/40 pl-4 my-4 text-muted-foreground text-sm italic">{trimmed.replace(/^> ?/gm, '')}</blockquote>;
    }
    // Bold and inline handling
    const html = trimmed
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
      .replace(/`(.+?)`/g, '<code class="px-1.5 py-0.5 rounded bg-secondary text-foreground text-xs">$1</code>');
    return <p key={i} className="text-foreground/80 text-sm leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function LegalPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/legal/${slug}?lang=${lang}`),
      axios.get(`${API}/legal?lang=${lang}`),
    ]).then(([contentRes, listRes]) => {
      setData(contentRes.data);
      setPages(listRes.data.pages || []);
    }).catch(() => {
      setData(null);
    }).finally(() => setLoading(false));
  }, [slug, lang]);

  const backLabel = lang === 'fr' ? 'Retour à l\'accueil' : 'Back to home';

  return (
    <div className="min-h-screen bg-background" data-testid="legal-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors" data-testid="legal-back-link">
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </Link>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !data ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">404</h2>
              <p className="text-muted-foreground">{lang === 'fr' ? 'Page introuvable.' : 'Page not found.'}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-4 gap-10">
              {/* Sidebar */}
              <aside className="md:col-span-1 hidden md:block" data-testid="legal-sidebar">
                <nav className="sticky top-24 space-y-1">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">
                    {lang === 'fr' ? 'Documents légaux' : 'Legal Documents'}
                  </p>
                  {pages.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/legal/${p.slug}`}
                      className={`block text-sm py-1.5 px-2 rounded transition-colors ${
                        p.slug === slug
                          ? 'text-foreground bg-secondary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`legal-nav-${p.slug}`}
                    >
                      {p.title}
                    </Link>
                  ))}
                </nav>
              </aside>
              {/* Content */}
              <motion.main
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="md:col-span-3"
                data-testid="legal-content"
              >
                {renderMarkdown(data.content)}
              </motion.main>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
